/**
 * Wraps a dynamic import factory with a one-shot page-reload guard.
 *
 * The PWA service worker caches chunk filenames by hash. After a new deploy the
 * hashes change and the cached names no longer exist on the server, so the
 * dynamic import throws a network/parse error. Reloading once forces the browser
 * to fetch the updated manifest and new chunk URLs. The sessionStorage flag
 * prevents an infinite reload loop when the chunk is genuinely absent (e.g. a
 * broken deploy) — after one failed attempt the error is re-thrown and the
 * normal error boundary takes over.
 */
export function withChunkReloadGuard<T>(
  reloadKey: string,
  importFn: () => Promise<T>,
): () => Promise<T> {
  return () =>
    importFn().catch((err: unknown) => {
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
        return new Promise<never>(() => {});
      }
      throw err;
    });
}
